// Coalesces requests that the sidebar and the dashboard make for the same data
// at the same moment.
//
// useSidebar's fetchStats says it outright -- "Fetch same data as Dashboard for
// accurate calculations" -- and both mount together on /dashboard, so each of
// those three endpoints was requested twice per load. Measured against
// production, the pairs start within a millisecond of each other
// (quizzes/student/assignments at 176ms and 177ms); they only *finish* far
// apart (317ms and 416ms) because the duplicates queue behind one another.
//
// Because the callers overlap, sharing the in-flight promise is enough -- no
// result is ever cached past settling, so nothing here can serve stale data or
// leak between tests.
//
// NOTE: callers share one resolved response object. Every current caller only
// reads from it; anything that needs to mutate a response must copy it first.

// A shared entry is only ever joined by callers that arrive alongside the
// original (the pairs above start ~1ms apart). Past this age we start a fresh
// request instead: an entry is released when its promise settles, so one that
// is still here after this long is a request that has hung, and joining it
// would hand every later caller the same hang rather than letting them retry.
const MAX_SHARE_AGE_MS = 2000;

// Shared by the dashboard's active-sessions card and the global check-in
// prompt, which are separate components that both request this on mount.
// A constant because each caller's URL carries its own cache-busting
// timestamp, so the URL itself can't be the key.
export const ATTENDANCE_TODAY_KEY = 'attendance/active-today';

const inFlight = new Map();

/**
 * Run `fn`, or join the identical request already in flight under `key`.
 * @param {string} key identifies the request (endpoint + any arguments)
 * @param {() => Promise<any>} fn performs the actual request
 */
export function sharedFetch(key, fn) {
  const existing = inFlight.get(key);
  if (existing && Date.now() - existing.at < MAX_SHARE_AGE_MS) {
    return existing.promise;
  }

  // Promise.resolve().then(fn) rather than fn(): a caller whose fn throws
  // synchronously would otherwise throw straight through sharedFetch, escaping
  // the Promise.allSettled its result was meant to land in and failing every
  // sibling request with it.
  const promise = Promise.resolve().then(fn);
  inFlight.set(key, { at: Date.now(), promise });

  // Drop it as soon as it settles, either way: the next caller starts fresh.
  // Guard against clearing a newer entry that replaced this one.
  const release = () => {
    const current = inFlight.get(key);
    if (current && current.promise === promise) {
      inFlight.delete(key);
    }
  };
  promise.then(release, release);

  return promise;
}
